// Currency helpers — symbols, formatting, location inference & approx conversion.
// Purely frontend. Codes are ISO (so Stripe/backend stay happy) but we DISPLAY symbols.
import React from "react";


// symbol: shown to users. space: put a space between symbol and number (alpha symbols).
// dec: default decimal places. rate: approximate USD value of 1 unit (for conversion only).
export const CURRENCY_INFO = {
  USD: { symbol: "$", space: false, dec: 2, rate: 1, name: "US Dollar" },
  EUR: { symbol: "€", space: false, dec: 2, rate: 1.08, name: "Euro" },
  GBP: { symbol: "£", space: false, dec: 2, rate: 1.27, name: "British Pound" },
  XOF: { symbol: "FCFA", space: true, dec: 0, rate: 0.00165, name: "West African CFA franc" },
  XAF: { symbol: "FCFA", space: true, dec: 0, rate: 0.00165, name: "Central African CFA franc" },
  RWF: { symbol: "RWF", space: true, dec: 0, rate: 0.00077, name: "Rwandan Franc" },
  NGN: { symbol: "₦", space: false, dec: 0, rate: 0.00065, name: "Nigerian Naira" },
  KES: { symbol: "KSh", space: true, dec: 0, rate: 0.0078, name: "Kenyan Shilling" },
  GHS: { symbol: "₵", space: false, dec: 2, rate: 0.067, name: "Ghanaian Cedi" },
  ZAR: { symbol: "R", space: true, dec: 2, rate: 0.055, name: "South African Rand" },
  UGX: { symbol: "USh", space: true, dec: 0, rate: 0.00027, name: "Ugandan Shilling" },
  TZS: { symbol: "TSh", space: true, dec: 0, rate: 0.0004, name: "Tanzanian Shilling" },
  EGP: { symbol: "E£", space: true, dec: 2, rate: 0.021, name: "Egyptian Pound" },
  MAD: { symbol: "DH", space: true, dec: 2, rate: 0.10, name: "Moroccan Dirham" },
  INR: { symbol: "₹", space: false, dec: 2, rate: 0.012, name: "Indian Rupee" },
  CNY: { symbol: "¥", space: false, dec: 2, rate: 0.14, name: "Chinese Yuan" },
  JPY: { symbol: "¥", space: false, dec: 0, rate: 0.0067, name: "Japanese Yen" },
  CAD: { symbol: "CA$", space: false, dec: 2, rate: 0.73, name: "Canadian Dollar" },
  AUD: { symbol: "A$", space: false, dec: 2, rate: 0.66, name: "Australian Dollar" },
  AED: { symbol: "AED", space: true, dec: 2, rate: 0.27, name: "UAE Dirham" },
};

// Curated order for the seller dropdown (Africa-first, matching this app's audience).
export const CURRENCY_ORDER = [
  "XOF", "XAF", "RWF", "NGN", "KES", "GHS", "ZAR", "UGX", "TZS", "EGP", "MAD",
  "USD", "EUR", "GBP", "INR", "CNY", "JPY", "CAD", "AUD", "AED",
];

export const CURRENCIES = CURRENCY_ORDER.map((code) => ({ code, ...CURRENCY_INFO[code] }));

export function symbolFor(code) {
  return (CURRENCY_INFO[code] || CURRENCY_INFO.USD).symbol;
}

// Format an amount for display: symbol + grouped number (thousands separators).
export function formatPrice(amount, code = "USD") {
  const info = CURRENCY_INFO[code] || CURRENCY_INFO.USD;
  const n = Number(amount);
  if (!isFinite(n)) return "—";
  const num = n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: info.dec });
  return info.space ? `${info.symbol} ${num}` : `${info.symbol}${num}`;
}

// Approximate conversion line, e.g. "≈ $15.80". Always shows the currency's decimals.
export function formatApprox(amount, code = "USD") {
  const info = CURRENCY_INFO[code] || CURRENCY_INFO.USD;
  const n = Number(amount);
  if (!isFinite(n)) return "";
  const num = n.toLocaleString(undefined, { minimumFractionDigits: info.dec, maximumFractionDigits: info.dec });
  return `≈ ${info.space ? `${info.symbol} ${num}` : `${info.symbol}${num}`}`;
}

export function convert(amount, from, to) {
  const f = CURRENCY_INFO[from] || CURRENCY_INFO.USD;
  const tt = CURRENCY_INFO[to] || CURRENCY_INFO.USD;
  const n = Number(amount);
  if (!isFinite(n)) return null;
  const fRate = usdPerUnit(from) ?? f.rate;
  const tRate = usdPerUnit(to) ?? tt.rate;
  if (!fRate || !tRate) return null;
  return (n * fRate) / tRate;
}

// --- Live FX rates (open.er-api.com, USD-based, cached 12h, static fallback) ---
// liveRates maps code -> USD value of 1 unit (same convention as CURRENCY_INFO.rate).
let liveRates = null;
let fxStarted = false;
const fxSubs = new Set();
const FX_KEY = "rc_fx_rates";
const FX_TTL = 12 * 60 * 60 * 1000;

function applyApiRates(apiRates) {
  // api gives "units of currency per 1 USD" -> invert to USD-per-unit
  const m = {};
  for (const [code, v] of Object.entries(apiRates || {})) {
    if (v > 0) m[code] = 1 / v;
  }
  if (Object.keys(m).length) { liveRates = m; fxSubs.forEach((fn) => fn()); }
}

function usdPerUnit(code) {
  return liveRates && liveRates[code] ? liveRates[code] : null;
}

export function ensureFxRates() {
  if (fxStarted) return;
  fxStarted = true;
  try {
    const cached = JSON.parse(localStorage.getItem(FX_KEY) || "null");
    if (cached && cached.at && Date.now() - cached.at < FX_TTL && cached.rates) {
      applyApiRates(cached.rates);
      return;
    }
  } catch {}
  const base = (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) || "";
  const token = (() => { try { return localStorage.getItem("rc_token"); } catch { return null; } })();
  fetch(`${base}/api/market/fx-rates`, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined)
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => {
      if (d && d.rates && Object.keys(d.rates).length) {
        applyApiRates(d.rates);
        try { localStorage.setItem(FX_KEY, JSON.stringify({ at: Date.now(), rates: d.rates })); } catch {}
      }
    })
    .catch(() => {});
}

// Hook: re-renders a component when live rates arrive.
export function useFxRates() {
  const [, force] = React.useState(0);
  React.useEffect(() => {
    ensureFxRates();
    const fn = () => force((x) => x + 1);
    fxSubs.add(fn);
    return () => fxSubs.delete(fn);
  }, []);
  return liveRates;
}

// Infer a currency code from a free-text location (country or city).
const LOCATION_MAP = [
  [/burkina|ouaga|bobo.?dioulasso/i, "XOF"],
  [/s[ée]n[ée]gal|dakar/i, "XOF"],
  [/c[ôo]te.?d.?ivoire|ivory\s*coast|abidjan|yamoussoukro/i, "XOF"],
  [/\bmali\b|bamako/i, "XOF"],
  [/\bniger\b|niamey/i, "XOF"],
  [/\btogo\b|lom[ée]/i, "XOF"],
  [/\bb[ée]nin\b|cotonou|porto.?novo/i, "XOF"],
  [/guinea.?bissau|bissau/i, "XOF"],
  [/cameroon|cameroun|douala|yaound[ée]/i, "XAF"],
  [/\bgabon\b|libreville/i, "XAF"],
  [/\bchad\b|tchad|n.?djamena/i, "XAF"],
  [/congo|brazzaville/i, "XAF"],
  [/central african|centrafrique|bangui/i, "XAF"],
  [/equatorial guinea|malabo/i, "XAF"],
  [/rwanda|kigali/i, "RWF"],
  [/nigeria|lagos|abuja|kano|ibadan/i, "NGN"],
  [/kenya|nairobi|mombasa/i, "KES"],
  [/ghana|accra|kumasi/i, "GHS"],
  [/south africa|johannesburg|cape town|pretoria|durban/i, "ZAR"],
  [/uganda|kampala/i, "UGX"],
  [/tanzania|dar\s*es\s*salaam|dodoma/i, "TZS"],
  [/egypt|cairo|alexandria/i, "EGP"],
  [/morocco|maroc|casablanca|rabat|marrakech/i, "MAD"],
  [/united states|\busa\b|\bus\b|new york|california|texas/i, "USD"],
  [/united kingdom|\buk\b|london|england|scotland/i, "GBP"],
  [/france|paris|germany|deutschland|berlin|spain|espa[ñn]a|madrid|italy|italia|rome|belgium|netherlands|portugal|lisbon/i, "EUR"],
  [/india|mumbai|delhi|bangalore|bengaluru/i, "INR"],
  [/china|shanghai|beijing|shenzhen/i, "CNY"],
  [/japan|tokyo|osaka/i, "JPY"],
  [/canada|toronto|montreal|vancouver/i, "CAD"],
  [/australia|sydney|melbourne/i, "AUD"],
  [/dubai|abu dhabi|emirates|\buae\b/i, "AED"],
];

export function guessCurrencyFromLocation(location) {
  if (!location) return null;
  for (const [re, code] of LOCATION_MAP) {
    if (re.test(location)) return code;
  }
  return null;
}

// Buyer's own currency (from their locale region), used only for the approx line.
const REGION_TO_CODE = {
  US: "USD", GB: "GBP", RW: "RWF", NG: "NGN", KE: "KES", GH: "GHS", ZA: "ZAR",
  UG: "UGX", TZ: "TZS", EG: "EGP", MA: "MAD", IN: "INR", CN: "CNY", JP: "JPY",
  CA: "CAD", AU: "AUD", AE: "AED",
  BF: "XOF", SN: "XOF", CI: "XOF", ML: "XOF", NE: "XOF", TG: "XOF", BJ: "XOF", GW: "XOF",
  CM: "XAF", GA: "XAF", TD: "XAF", CG: "XAF", CF: "XAF", GQ: "XAF",
  FR: "EUR", DE: "EUR", ES: "EUR", IT: "EUR", BE: "EUR", NL: "EUR", PT: "EUR",
};

export function getBuyerCurrency() {
  try {
    const saved = localStorage.getItem("rc_buyer_currency");
    if (saved && CURRENCY_INFO[saved]) return saved;
    const locs = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || ""];
    for (const l of locs) {
      const region = (String(l).split("-")[1] || "").toUpperCase();
      if (REGION_TO_CODE[region]) return REGION_TO_CODE[region];
    }
  } catch {}
  return "USD";
}
